/** Shared building blocks: Card, Ring, TargetBar, StatTile, Segmented, Modal, Empty. */
import { useEffect, type ReactElement, type ReactNode } from 'react';
import { Icon } from './Icon';

export function Card({ title, sub, right, children, className }: { title?: string; sub?: string; right?: ReactNode; children: ReactNode; className?: string }): ReactElement {
  return (
    <section className={`card ${className ?? ''}`}>
      {title !== undefined && (
        <div className="card-head">
          <h2>{title}</h2>
          {sub !== undefined && <span className="sub">{sub}</span>}
          {right !== undefined && <div className="right">{right}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

export interface RingSegment { value: number; color: string; label?: string }

/**
 * Donut ring. Pass one segment for a progress ring (value/max) or several for
 * a split (they are drawn in order and scaled to `max`).
 */
export function Ring({ segments, max, size = 120, thickness = 12, children, title }: { segments: RingSegment[]; max: number; size?: number; thickness?: number; children?: ReactNode; title?: string }): ReactElement {
  const r: number = (size - thickness) / 2;
  const c: number = 2 * Math.PI * r;
  const safeMax: number = max > 0 ? max : 1;
  let offset: number = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={title}>
      {title !== undefined && <title>{title}</title>}
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--track)" strokeWidth={thickness} />
      {segments.map((s, i) => {
        const frac: number = Math.max(0, Math.min(1 - offset, s.value / safeMax));
        const el = (
          <circle key={i} cx={size / 2} cy={size / 2} r={r} fill="none" stroke={s.color} strokeWidth={thickness}
            strokeDasharray={`${frac * c} ${c}`} strokeDashoffset={-offset * c} strokeLinecap={segments.length === 1 ? 'round' : 'butt'}
            transform={`rotate(-90 ${size / 2} ${size / 2})`} style={{ transition: 'stroke-dasharray .5s ease' }} />
        );
        offset += frac;
        return el;
      })}
      {children !== undefined && (
        <foreignObject x={thickness} y={thickness} width={size - thickness * 2} height={size - thickness * 2}>
          <div style={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center', textAlign: 'center', lineHeight: 1.1 }}>{children}</div>
        </foreignObject>
      )}
    </svg>
  );
}

export function TargetBar({ name, value, target, unit, color, digits = 0, kind = 'min' }: { name: string; value: number; target: number | null; unit: string; color: string; digits?: number; kind?: 'min' | 'max' }): ReactElement {
  const pct: number = target && target > 0 ? value / target : 0;
  const over: boolean = pct > 1;
  const fill: string = kind === 'max' && over ? 'var(--bad)' : color;
  return (
    <div className="tbar">
      <div className="name">{name}</div>
      <div className={`bar ${over ? 'over' : ''}`} role="progressbar" aria-valuenow={Math.round(pct * 100)} aria-valuemin={0} aria-valuemax={100} aria-label={name}>
        <i style={{ width: `${Math.min(100, pct * 100)}%`, background: fill }} />
      </div>
      <div className="nums num">
        <span>{value.toFixed(digits)} / {target !== null ? `${target.toFixed(digits)} ${unit}` : `— ${unit}`}</span>
        <span style={{ color: kind === 'max' && over ? 'var(--bad)' : undefined }}>{target ? `${Math.round(pct * 100)}%` : 'no target'}</span>
      </div>
    </div>
  );
}

export function StatTile({ label, value, unit, delta, deltaClass }: { label: string; value: string; unit?: string; delta?: string; deltaClass?: string }): ReactElement {
  return (
    <div className="tile">
      <div className="label">{label}</div>
      <div className="value num">{value}{unit !== undefined && <small>{unit}</small>}</div>
      {delta !== undefined && <div className={`delta ${deltaClass ?? 'muted'}`}>{delta}</div>}
    </div>
  );
}

export function Segmented<T extends string | number>({ options, value, onChange }: { options: ReadonlyArray<{ value: T; label: string }>; value: T; onChange: (v: T) => void }): ReactElement {
  return (
    <div className="seg" role="tablist">
      {options.map((o) => (
        <button key={String(o.value)} type="button" role="tab" aria-selected={o.value === value} className={o.value === value ? 'on' : ''} onClick={() => onChange(o.value)}>{o.label}</button>
      ))}
    </div>
  );
}

export function Modal({ title, onClose, children, footer, width }: { title: string; onClose: () => void; children: ReactNode; footer?: ReactNode; width?: number }): ReactElement {
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prev: string = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);
  return (
    <div className="overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" role="dialog" aria-modal="true" aria-label={title} style={width ? { width: `min(${width}px, 100%)` } : undefined}>
        <div className="modal-head">
          <h2>{title}</h2>
          <button type="button" className="icon-btn" style={{ marginLeft: 'auto' }} onClick={onClose} aria-label="Close"><Icon name="close" size={18} /></button>
        </div>
        <div className="modal-body">{children}</div>
        {footer !== undefined && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

export function Empty({ title, children }: { title: string; children?: ReactNode }): ReactElement {
  return <div className="empty"><strong>{title}</strong>{children}</div>;
}

export function Spinner(): ReactElement {
  return <span className="spinner" aria-label="Loading" />;
}

export function Skeleton({ height = 120 }: { height?: number }): ReactElement {
  return <div className="skeleton" style={{ height }} />;
}
